import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, FileText, ShieldAlert, XCircle } from 'lucide-react';
import Button from '../components/UI/Button';

const normalizeError = (error: string | null): string => {
  const value = (error || '').trim();
  if (!value) return 'پرداخت در درگاه بانکی تایید نشد.';

  const lowered = value.toLowerCase();
  if (lowered.includes('verification') || lowered.includes('failed')) {
    return 'تایید پرداخت توسط بانک انجام نشد.';
  }

  return value;
};

const PaymentResultPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const status = (searchParams.get('status') || '').toLowerCase();
  const isSuccess = status === 'success';
  const ref = (searchParams.get('ref') || '').trim();
  const invoice = (searchParams.get('invoice') || ref || '---').trim();
  const amount = Number(searchParams.get('amount') || '0');
  const error = normalizeError(searchParams.get('error'));
  const createdAt = new Date().toLocaleString('fa-IR');

  const invoiceCardClass = isSuccess
    ? 'rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3'
    : 'rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3';

  const invoiceLabelClass = isSuccess ? 'text-xs text-emerald-700 mb-1' : 'text-xs text-rose-700 mb-1';
  const invoiceValueClass = isSuccess
    ? 'font-mono text-base font-black text-emerald-900 uppercase break-all'
    : 'font-mono text-base font-black text-rose-900 uppercase break-all';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" dir="rtl">
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-2xl overflow-hidden">
        <div className={`px-8 pt-10 pb-7 flex flex-col items-center text-center ${isSuccess ? 'bg-emerald-50' : 'bg-rose-50'}`}>
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-5 shadow-md ${isSuccess ? 'bg-white text-emerald-600 shadow-emerald-100' : 'bg-white text-rose-600 shadow-rose-100'}`}>
            {isSuccess ? <CheckCircle size={46} strokeWidth={2.4} /> : <XCircle size={46} strokeWidth={2.4} />}
          </div>

          <h1 className={`text-2xl font-black mb-3 ${isSuccess ? 'text-emerald-900' : 'text-rose-900'}`}>
            {isSuccess ? 'پرداخت با موفقیت تایید شد' : 'پرداخت ناموفق بود'}
          </h1>

          <p className={`text-sm md:text-base leading-7 font-semibold max-w-xl ${isSuccess ? 'text-emerald-700' : 'text-rose-700'}`}>
            {isSuccess
              ? 'تراکنش شما با موفقیت ثبت شد. آگهی شما پس از تایید ادمین نمایش داده می‌شود.'
              : 'پرداخت از سمت بانک تایید نشد. اگر مبلغی از حساب شما کسر شده باشد، حداکثر تا 72 ساعت آینده به حساب شما باز می‌گردد.'}
          </p>
        </div>

        <div className="p-8 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={invoiceCardClass}>
              <div className={invoiceLabelClass}>شماره فاکتور</div>
              <div className={invoiceValueClass}>{invoice}</div>
            </div>

            <div className={invoiceCardClass}>
              <div className={invoiceLabelClass}>شماره پیگیری بانک</div>
              <div className={invoiceValueClass}>{ref || '---'}</div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="text-xs text-gray-500 mb-1">وضعیت پرداخت</div>
              <div className={`text-sm font-black ${isSuccess ? 'text-emerald-700' : 'text-rose-700'}`}>
                {isSuccess ? 'موفق و تایید شده' : 'ناموفق'}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="text-xs text-gray-500 mb-1">زمان ثبت نتیجه</div>
              <div className="text-sm font-bold text-gray-700" dir="ltr">{createdAt}</div>
            </div>
          </div>

          {Number.isFinite(amount) && amount > 0 && (
            <div className={`${isSuccess ? 'border-emerald-100 bg-emerald-50' : 'border-rose-100 bg-rose-50'} rounded-2xl border px-4 py-3`}>
              <div className={`text-xs mb-1 ${isSuccess ? 'text-emerald-700' : 'text-rose-700'}`}>مبلغ تراکنش</div>
              <div className={`font-black text-lg ${isSuccess ? 'text-emerald-900' : 'text-rose-900'}`}>
                {amount.toLocaleString('fa-IR')} تومان
              </div>
            </div>
          )}

          {isSuccess ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800 text-sm font-semibold flex items-start gap-2">
              <FileText size={18} className="mt-0.5 shrink-0" />
              <span>فاکتور شما ثبت شد. آگهی پس از تایید ادمین منتشر می‌شود.</span>
            </div>
          ) : (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm font-semibold flex items-start gap-2">
              <ShieldAlert size={18} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="pt-2 space-y-3">
            <Button
              fullWidth
              size="lg"
              className={isSuccess ? 'py-4 rounded-2xl bg-emerald-600 hover:bg-emerald-700' : 'py-4 rounded-2xl bg-rose-600 hover:bg-rose-700'}
              onClick={() => navigate('/dashboard')}
            >
              برو به پنل کاربری
            </Button>

            <button
              type="button"
              className="w-full py-2 text-gray-600 hover:text-teal-600 flex items-center justify-center gap-2 text-sm font-bold transition-colors"
              onClick={() => navigate('/')}
            >
              <ArrowLeft size={18} />
              بازگشت به صفحه اصلی
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentResultPage;
